import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'
import { Accept, FileRejection, useDropzone } from 'react-dropzone'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import { Upload } from '@workwave-tidal/core/illustrations/Upload'
import { fileOnDropRejectedMessage } from '@user/utils/AttachmentRejectMessage'
import FormHelperText from '@mui/material/FormHelperText'
import { MAX_FILE_SIZE } from '../constants'
import { validateFileName } from '../validateFileName'

interface Props {
  fileMimeTypes: Accept
  onFilesSelected: (files: FileList) => void
  disabled?: boolean
}

export const FileSelect = (props: Props) => {
  const { onFilesSelected, disabled = false } = props

  // ========== S T A T E ==========
  const [fileRejections, setFileRejections] = useState<FileRejection[]>([])
  const onDrop = useCallback(
    (acceptedFiles) => {
      setFileRejections([])
      onFilesSelected(acceptedFiles)
    },
    [onFilesSelected],
  )

  const getDropReject = (fileRejections: FileRejection[]) => {
    setFileRejections(fileRejections)
  }

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    maxSize: MAX_FILE_SIZE,
    onDrop,
    onDropRejected: getDropReject,
    accept: props.fileMimeTypes,
    validator: validateFileName,
    noClick: true,
    disabled: disabled,
  })

  const error = useMemo(() => fileRejections?.length > 0, [fileRejections])

  return (
    <Box>
      <Card
        sx={(theme) => ({
          borderWidth: '1px',
          borderColor: error ? '#D63B1F' : isDragActive ? '#1565C0' : '#D3D7DC',
          borderStyle: isDragActive ? 'solid' : 'dashed',
          minHeight: theme.spacing(19.2),
          backgroundColor: disabled
            ? theme.palette.grey[100]
            : isDragActive
              ? '#E1EBF7'
              : '',
          '&:hover': {
            borderColor: !disabled && !error && '#091B2A0A',
            backgroundColor: !disabled ? theme.palette.grey[100] : '',
          },
        })}
        variant="outlined"
        {...getRootProps()}
      >
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <input {...getInputProps()} />
          <Upload size={88} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Typography variant="h5">Upload new Attachment</Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                color: (theme) => theme.palette.text.secondary,
                mt: 0.8,
              }}
            >
              <Typography variant="body1">Drag files here or</Typography>

              <Button
                disabled={disabled}
                variant="text"
                onClick={open}
                sx={{ p: 0, px: 0.5 }}
                data-test-id="file-select-button"
              >
                <Typography variant="body1">
                  select from your computer
                </Typography>
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
      {error ? (
        <FormHelperText sx={{ ml: 1 }} error>
          {fileOnDropRejectedMessage(fileRejections[0].errors[0])}
        </FormHelperText>
      ) : null}
    </Box>
  )
}

FileSelect.displayName = 'FileSelect'
